import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/** IntersectionObserver that works inside the editor's device iframe (uses the element's own window). */
export const useInView = (ref, { once = true, threshold = 0.12, enabled = true } = {}) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return undefined;
    const win = el.ownerDocument?.defaultView || window;
    if (!win.IntersectionObserver) { setInView(true); return undefined; }
    const io = new win.IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); if (once) io.disconnect(); } else if (!once) setInView(false);
    }, { threshold, rootMargin: '0px 0px -6% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, once, threshold, enabled]);
  return inView;
};

/** Scroll-reveal wrapper: CSS classes do the animating (see site-theme.css). */
export const Reveal = ({ effect, className = '', children, ...rest }) => {
  const ref = useRef(null);
  const active = !!effect && effect !== 'none';
  const on = useInView(ref, { enabled: active });
  return <div ref={ref} className={active ? `${className} reveal reveal-${effect} ${on ? 'in-view' : ''}` : className} {...rest}>{children}</div>;
};

const parseNumber = (raw) => {
  const m = String(raw).match(/^([^\d]*)([\d.,]+)(.*)$/);
  if (!m) return null;
  const num = m[2];
  let sep = '';
  let decimals = 0;
  let n;
  if (/^\d{1,3}([.,]\d{3})+$/.test(num)) { sep = num.match(/[.,]/)[0]; n = Number(num.replace(/[.,]/g, '')); }
  else if (/^\d+[.,]\d+$/.test(num)) { const d = num.match(/[.,]/)[0]; decimals = num.split(d)[1].length; n = Number(num.replace(d, '.')); sep = d === ',' ? '.' : ','; }
  else if (/^\d+$/.test(num)) n = Number(num);
  else return null;
  const fmt = (v) => {
    const fixed = v.toFixed(decimals);
    const [i, f] = fixed.split('.');
    const grouped = sep && !decimals ? i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : i;
    return f != null ? `${grouped}${sep === '.' ? ',' : '.'}${f}` : grouped;
  };
  return { prefix: m[1], suffix: m[3], n, fmt };
};

/** Animated number ("500+", "Rp 1.200", "4,9") counting up when scrolled into view. */
export const CountUp = ({ value, enabled, className, style }) => {
  const ref = useRef(null);
  const on = useInView(ref);
  const parsed = enabled ? parseNumber(value) : null;
  const [disp, setDisp] = useState(parsed ? `${parsed.prefix}${parsed.fmt(0)}${parsed.suffix}` : String(value ?? ''));
  useEffect(() => {
    if (!parsed) { setDisp(String(value ?? '')); return undefined; }
    if (!on) return undefined;
    const win = ref.current?.ownerDocument?.defaultView || window;
    const t0 = win.performance.now();
    const dur = 1500;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setDisp(`${parsed.prefix}${parsed.fmt(parsed.n * e)}${parsed.suffix}`);
      if (p < 1) raf = win.requestAnimationFrame(tick);
    };
    raf = win.requestAnimationFrame(tick);
    return () => win.cancelAnimationFrame(raf);
  }, [on, value, enabled]); // eslint-disable-line
  return <span ref={ref} className={className} style={style} data-testid="site-countup">{disp}</span>;
};

/** Cycles through words with a vertical flip. */
export const RotatingWords = ({ words, animated, className = '', style }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!animated || words.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % words.length), 2400);
    return () => clearInterval(t);
  }, [animated, words.length]);
  if (!words.length) return null;
  if (!animated) return <span className={className} style={style}>{words[0]}</span>;
  return (
    <span className={`relative inline-grid overflow-hidden align-bottom ${className}`} style={style} data-testid="site-rotating-words">
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap">{words.reduce((a, b) => (a.length >= b.length ? a : b))}</span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span key={words[i]} className="col-start-1 row-start-1 whitespace-nowrap" initial={{ y: '100%', opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: '-100%', opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>{words[i]}</motion.span>
      </AnimatePresence>
    </span>
  );
};

/** Soft animated gradient blobs for the "mesh" hero. */
export const MeshBlobs = ({ animated }) => {
  const blobs = [
    { c: 'rgba(var(--site-primary-rgb),0.35)', s: 520, x: '-10%', y: '-20%', dx: 60, dy: 40, d: 18 },
    { c: 'rgba(var(--site-secondary-rgb),0.35)', s: 460, x: '60%', y: '-10%', dx: -50, dy: 60, d: 22 },
    { c: 'rgba(var(--site-primary-rgb),0.22)', s: 420, x: '30%', y: '55%', dx: 40, dy: -50, d: 20 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden data-testid="site-mesh">
      {blobs.map((b, i) => (
        <motion.div key={i} className="absolute rounded-full" style={{ width: b.s, height: b.s, left: b.x, top: b.y, background: b.c, filter: 'blur(70px)' }}
          animate={animated ? { x: [0, b.dx, 0], y: [0, b.dy, 0], scale: [1, 1.08, 1] } : undefined} transition={{ duration: b.d, repeat: Infinity, ease: 'easeInOut' }} />
      ))}
    </div>
  );
};

/** Parallax: translates the element as its own window scrolls. */
export const useParallax = (ref, enabled, speed = 0.15) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) { if (el) el.style.transform = ''; return undefined; }
    const win = el.ownerDocument.defaultView;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const center = r.top + r.height / 2 - win.innerHeight / 2;
      el.style.transform = `translate3d(0, ${(-center * speed).toFixed(1)}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = win.requestAnimationFrame(update); };
    update();
    win.addEventListener('scroll', onScroll, { passive: true });
    win.addEventListener('resize', onScroll);
    return () => { win.removeEventListener('scroll', onScroll); win.removeEventListener('resize', onScroll); if (raf) win.cancelAnimationFrame(raf); el.style.transform = ''; };
  }, [ref, enabled, speed]);
};

/** Infinite horizontal marquee (CSS keyframes, pauses on hover). */
export const Marquee = ({ children, speed = 30, className = '' }) => (
  <div className={`site-marquee ${className}`} style={{ '--marquee-duration': `${speed}s` }} data-testid="site-marquee">
    <div className="site-marquee-track">{children}{children}</div>
  </div>
);

/** Auto-playing carousel with dots; renders `items.length` slides via `render(item, i)`. */
export const Carousel = ({ items, render, interval = 4500, animated }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = items.length;
  useEffect(() => {
    if (!animated || paused || n < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), interval);
    return () => clearInterval(t);
  }, [animated, paused, n, interval]);
  if (!n) return null;
  const idx = Math.min(i, n - 1);
  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="site-carousel">
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={idx} initial={animated ? { opacity: 0, x: 40 } : false} animate={{ opacity: 1, x: 0 }} exit={animated ? { opacity: 0, x: -40 } : undefined} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            {render(items[idx], idx)}
          </motion.div>
        </AnimatePresence>
      </div>
      {n > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          {items.map((_, j) => <button key={j} type="button" aria-label={`slide ${j + 1}`} onClick={() => setI(j)} className="h-2 rounded-full transition-all" style={{ width: j === idx ? 24 : 8, background: j === idx ? 'var(--site-primary)' : 'rgba(var(--site-primary-rgb),0.25)' }} data-testid={`site-carousel-dot-${j}`} />)}
        </div>
      )}
    </div>
  );
};

/** Animated expand/collapse (FAQ). */
export const Collapse = ({ open, animated, children }) => {
  if (!animated) return open ? <div>{children}</div> : null;
  return (
    <AnimatePresence initial={false}>
      {open && <motion.div key="c" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>{children}</motion.div>}
    </AnimatePresence>
  );
};
